import { Tag } from 'antd';
import md5 from 'blueimp-md5';
import ErrorPlaceholder from 'components/Error';
import { type UserLogsFilterData, useUserLogs } from 'hooks/useUserLogs';
import { defer } from 'lodash';
import { DateTime } from 'luxon';
import { FormSpy } from 'react-final-form';
import type { RouteComponentProps } from 'react-router';
import type { UserLogRecord } from 'types/userLogs';
import { Card, DateRange, Form, Table } from 'ui-kit';
import { getColor } from 'utils/colors';
import { mixed } from 'yup';
import { useTranslation } from 'react-i18next';
import { useMemo, useCallback } from 'react';
import styles from './UserLogsPage.module.scss';

const INITIAL_DATA = {
  period: {
    From: DateTime.local().startOf('day').minus({ day: 1 }).toMillis(),
    To: DateTime.local().endOf('day').toMillis()
  },
  type: 'AllEvents'
};

function Filter({
  onChange
}: {
  onChange: (data: UserLogsFilterData) => void;
}) {
  return (
    <Form<UserLogsFilterData>
      resourceName="userLog"
      schema={mixed()}
      onSubmit={onChange}
      initialValues={INITIAL_DATA}
      className={styles.filter}
      hideModal
    >
      <DateRange name="period" />
      <FormSpy<UserLogsFilterData> subscription={{ values: true }}>
        {({ values }) => {
          defer(() => {
            onChange(values);
          });
          return null;
        }}
      </FormSpy>
    </Form>
  );
}

export function UserLogsPage({
  match: {
    params: { userId: routeUserId }
  }
}: RouteComponentProps<{ userId: string }>) {
  // routeUserId - это пользователь из url'a
  const { setFilter, logs, usernames, loading, error } =
    useUserLogs(routeUserId);

  const { t } = useTranslation(['userLog']);

  const ACTION_DESCRIPTIONS = useMemo(
    () => [
      {
        pattern: { method: 'POST', urlPattern: 'devices' },
        description: t('userLog:actions.update_device_settings')
      },
      {
        pattern: { method: 'PUT', urlPattern: 'lastservice' },
        description: t('userLog:actions.update_service_date')
      },
      {
        pattern: { method: 'PUT', urlPattern: 'cartridges' },
        description: t('userLog:actions.update_cartridge')
      }
    ],
    [t]
  );

  const getActionDescription = useCallback(
    (method: string, url: string): string => {
      const matchedPattern = ACTION_DESCRIPTIONS.find(
        ({ pattern }) =>
          pattern.method === method && url.includes(pattern.urlPattern)
      );
      return matchedPattern?.description || '';
    },
    [ACTION_DESCRIPTIONS]
  );

  if (error) {
    return (
      <ErrorPlaceholder
        message=""
        description={error instanceof Error ? error.message : 'Error'}
      />
    );
  }

  return (
    <Card className={styles.container}>
      <div>
        <div className="filter">
          <Filter onChange={setFilter} />
        </div>
      </div>
      <Table
        data={logs}
        loading={loading}
        className={styles.logs}
        columns={[
          {
            label: t('columns.time'),
            name: 'CreatedAt',
            render: (CreatedAt: number) =>
              DateTime.fromMillis(CreatedAt)
                .setLocale('ru')
                .toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)
          },
          {
            label: t('columns.executor'),
            name: 'UserId',
            render: (_: string, record: UserLogRecord) =>
              usernames[record.UserId] || record.UserId
          },
          {
            label: t('columns.target'),
            name: 'TargetUserId',
            render: (_: string, record: UserLogRecord) =>
              record.TargetUserId
                ? usernames[record.TargetUserId] || record.TargetUserId
                : '-'
          },
          {
            label: t('columns.request'),
            name: 'Method',
            render: (_: string, record: UserLogRecord) => (
              <div className={styles.request}>
                <Tag color={getColor(md5(record.Method))}>{record.Method}</Tag>
                {record.Url}
              </div>
            )
          },
          {
            label: t('columns.explanation'),
            name: 'Explanation',
            render: (_: string, record: UserLogRecord) =>
              getActionDescription(record.Method, record.Url)
          }
        ]}
      />
    </Card>
  );
}
